// Accessible-swatch helpers for the per-Segment Wallpaper picker.
//
// The gradient/pattern swatch tiles in the Segment background picker were
// click-only `<Box onClick>` / styled `<div onClick>` elements: not keyboard
// reachable, no screen-reader name, no visible focus ring, and selection was
// signalled by border COLOUR alone (fails WCAG — colour-only signal +
// keyboard-inoperable). These pure helpers turn each tile into a proper
// keyboard-operable radio option without touching the shared ColorSwatch.
//
// Kept as a pure module so the keyboard/aria logic is unit-testable and shared
// across every swatch grid in the picker (DRY — one behaviour, not copied).

// Focus-ring styling for the swatch wrapper (MUI `sx`). Uses :focus-visible so
// the ring only shows for keyboard users, not on mouse click.
export const SWATCH_FOCUS_RING_SX = {
  outline: "none",
  borderRadius: "50%",
  cursor: "pointer",
  "&:focus-visible": {
    outline: "2px solid #81459B",
    outlineOffset: "2px",
  },
};

// True when a keyboard event should activate the swatch (Enter or Space).
export function isSwatchActivationKey(key) {
  return key === "Enter" || key === " " || key === "Spacebar";
}

// Returns the a11y props to spread onto a swatch wrapper so it behaves as a
// radio option: role, focusability, accessible name, selected state, and an
// Enter/Space keyboard activator mirroring the existing onClick.
//
// - label:      accessible name (required; falls back to a generic string)
// - selected:   whether this swatch is the current selection (aria-checked)
// - onActivate: called on Enter/Space (pass the same handler as onClick)
export function getSwatchA11yProps({ label, selected, onActivate } = {}) {
  return {
    role: "radio",
    tabIndex: 0,
    "aria-label": label || "Background option",
    "aria-checked": Boolean(selected),
    onKeyDown: (event) => {
      if (!event) return;
      if (isSwatchActivationKey(event.key)) {
        // Prevent Space from scrolling the pane / Enter from submitting a form.
        if (typeof event.preventDefault === "function") event.preventDefault();
        if (typeof onActivate === "function") onActivate(event);
      }
    },
  };
}

// Props for a swatch-grid container so assistive tech announces the group and
// its purpose (a set of mutually-exclusive background options).
export function getSwatchGroupProps(label) {
  return {
    role: "radiogroup",
    "aria-label": label || "Background options",
  };
}
